import {
  Box,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from "@chakra-ui/react";
import { RevealVertically } from "@raiden/library-ui/components/Animations/RevealVertically";
import FormControlRHF from "@raiden/library-ui/components/ReactHookForm/FormControlRHF";
import FormObserver from "@raiden/library-ui/components/ReactHookForm/FormObserver";
import { Slider } from "@raiden/library-ui/components/Slider";
import { CAMPAIGNS_DATA_FRONT } from "@raiden/library-ui/constants/campaigns";
import { useSearchInfiniteContext } from "@raiden/library-ui/contexts/SearchInfinite/useSearchInfiniteContext";
import useTranslate from "@raiden/library-ui/hooks/useTranslate";
import { useIntl } from "react-intl";
import { CenteredContent } from "../../../components/CenteredContent";
import { Icon } from "@raiden/library-ui/components/Icon";

/**
 * @typedef {object} Props
 * @property {import("@raiden/library-ui/types/Campaign").CampaignType} campaignType
 * @property {import("swr").SWRResponse<import("@raiden/library-ui/types/Api/ApiResponse").ApiResponse<import("@raiden/library-ui/types/Configuration").Configuration>>} [swrResponseConfiguration]
 */
/**
 * @param {Props} props
 */
export function Filters({ campaignType, swrResponseConfiguration }) {
  const intl = useIntl();

  /** @type {import("@raiden/library-ui/contexts/SearchInfinite/useSearchInfiniteContext").UseSearchInfiniteReturn<import(".").CampaignSearchFormValues>} */
  const { form } = useSearchInfiniteContext();

  const translate = useTranslate();

  const campaignsData = CAMPAIGNS_DATA_FRONT[campaignType];

  return (
    <Box>
      <Box px="1rem">
        <CenteredContent>
          <Box backgroundColor="#fff" borderRadius="10px" w="full">
            <FormControlRHF
              name="term"
              render={(field) => (
                <InputGroup flexGrow={1}>
                  <Input
                    {...field}
                    autoComplete="off"
                    placeholder={
                      campaignsData &&
                      intl.formatMessage(campaignsData.searchTermPlaceholder)
                    }
                  />

                  <InputLeftElement
                    color="gray.500"
                    zIndex="auto"
                    pointerEvents="none">
                    <Icon icon="ms_search" size="24px" />
                  </InputLeftElement>
                </InputGroup>
              )}
            />
          </Box>
        </CenteredContent>
      </Box>

      <RevealVertically
        isVisible={swrResponseConfiguration?.data !== undefined}>
        <Box pt="1rem">
          <CenteredContent
            maxWidth={({ defaultMaxWidth }) =>
              `calc(${defaultMaxWidth} + 2rem)`
            }>
            <FormObserver control={form.control} name="categoryId">
              {(categoryId) => (
                <Slider scrollSnap={false}>
                  {swrResponseConfiguration?.data?.data.campaigns_categories?.[
                    campaignType
                  ].map((category) => (
                    <HStack
                      as="button"
                      flexShrink={0}
                      spacing="0.375rem"
                      p="0.75rem"
                      overflow="hidden"
                      borderRadius="full"
                      borderWidth="1px"
                      borderColor={
                        categoryId === String(category.id)
                          ? "#000"
                          : "transparent"
                      }
                      onClick={(e) => {
                        form.setValue(
                          "categoryId",
                          categoryId === String(category.id)
                            ? ""
                            : String(category.id),
                          {
                            shouldDirty: true,
                          },
                        );
                        setTimeout(() => {
                          /** @type {HTMLDivElement} */ (
                            e.target
                          ).scrollIntoView({
                            inline: "center",
                            block: "nearest",
                            behavior: "smooth",
                          });
                        }, 0);
                      }}
                      key={category.id}>
                      {category.icon && (
                        <Icon icon={category.icon} size="1rem" />
                      )}

                      <Text variant="text-xs" noOfLines={1}>
                        {translate(category.title)}
                      </Text>
                    </HStack>
                  ))}
                </Slider>
              )}
            </FormObserver>
          </CenteredContent>
        </Box>
      </RevealVertically>
    </Box>
  );
}
